function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }

function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }

import fetch from 'unfetch';
import { logger } from '@immediate_media/logger';
import Cookies from 'js-cookie';
import { getTokenProperty } from './utils';
import { LOGIN_FLAG_COOKIE_NAME } from './constants';
export var getIsLoggedIn = function getIsLoggedIn() {
  return Cookies.get(LOGIN_FLAG_COOKIE_NAME) === '1';
};
/**
 * @typedef {{
 *  email: string;
 *  idToken: string;
 *  idp: string;
 *  role: string | string[];
 *  userId: string;
 *  vanityId: string | string[];
 * }} UserInfo
 *
 * @typedef {keyof UserInfo} Field
 *
 * @typedef {Omit<UserInfo, 'idToken' | 'userId' | 'vanityId'> & {
 *  sub: string;
 *  vanity_id: string | string[];
 * }} ClaimType
 *
 * @typedef {keyof ClaimType} Claim
 */

/**
 * Normalise the vanity id claim in the access token.
 *
 * There's an unusual issue in the Identity data where a few thousand users
 * have multiple vanity ID claims. Here just pick one (the sort being to
 * make it deterministic).
 *
 * @param {ClaimType['vanity_id']} vanityId
 * @returns {string}
 */

export var normaliseVanityId = function normaliseVanityId(vanityId) {
  if (Array.isArray(vanityId)) {
    return vanityId.sort()[0];
  }

  return vanityId;
};
/**
 * Get user info from the server. The `fields` argument is required and the endpoint will return
 * only the values corresponding to those fields.
 * @template {Field} [TField]
 * @param {TField | TField[]} fields The fields to fetch provided as a string or array of strings.
 * @returns {Promise<
 *  TField extends Field
 *    ? Record<TField, UserInfo[TField]>
 *    : null
 *  | null
 * >} The user info.
 */

export var getUserInfo = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(fields) {
    var data, fieldsArray, params, response, status, statusText, failedFields;
    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (fields) {
              _context.next = 2;
              break;
            }

            throw new Error('The `fields` argument is required to fetch user info.');

          case 2:
            _context.prev = 2;
            fieldsArray = Array.isArray(fields) ? fields : [fields];
            params = new URLSearchParams(fieldsArray.map(function (field) {
              return ['fields', field];
            }));
            _context.next = 7;
            return fetch("/auth/user-info?".concat(params.toString()));

          case 7:
            response = _context.sent;

            if (response.ok) {
              _context.next = 12;
              break;
            }

            status = response.status, statusText = response.statusText;
            logger.error("Failed to fetch user info. ".concat(status, " - ").concat(statusText));
            return _context.abrupt("return", null);

          case 12:
            _context.next = 14;
            return response.json();

          case 14:
            data = _context.sent;
            failedFields = fieldsArray.map(function (field) {
              var _data;

              if (!((_data = data) === null || _data === void 0 ? void 0 : _data[field])) {
                return field;
              }

              return null;
            }).filter(Boolean);

            if (!failedFields.length) {
              _context.next = 19;
              break;
            }

            logger.error("Failed to fetch user info. Missing fields: ".concat(failedFields.join(', '), "."));
            return _context.abrupt("return", null);

          case 19:
            _context.next = 25;
            break;

          case 21:
            _context.prev = 21;
            _context.t0 = _context["catch"](2);
            logger.error("Failed to fetch user info. ".concat(_context.t0.message), _context.t0);
            return _context.abrupt("return", null);

          case 25:
            return _context.abrupt("return", data);

          case 26:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[2, 21]]);
  }));

  return function getUserInfo(_x) {
    return _ref.apply(this, arguments);
  };
}();
/**
 * Get a specific user claim from the access token.
 * @template {Claim} TClaim
 * @param {TClaim} claim
 * @param {string} [accessToken] - The access token (server-side only).
 * @returns {Promise<ClaimType[TClaim] | undefined>}
 */

var getUserClaim = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(claim, accessToken) {
    var claimFromToken, isLoggedIn, field, userInfo;
    return regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            if (!accessToken) {
              _context2.next = 4;
              break;
            }

            if (typeof window !== 'undefined') {
              logger.warn("E49860: Access token used client-side for claim: \"".concat(claim, "\"."), {
                path: window.location.pathname,
                trace: new Error().stack
              });
            }

            claimFromToken = getTokenProperty(accessToken, claim);
            return _context2.abrupt("return", claimFromToken);

          case 4:
            isLoggedIn = getIsLoggedIn();

            if (isLoggedIn) {
              _context2.next = 7;
              break;
            }

            return _context2.abrupt("return", undefined);

          case 7:
            field = {
              sub: 'userId',
              vanity_id: 'vanityId'
            }[claim] || claim;
            _context2.next = 10;
            return getUserInfo(field);

          case 10:
            userInfo = _context2.sent;
            return _context2.abrupt("return", userInfo === null || userInfo === void 0 ? void 0 : userInfo[field]);

          case 12:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getUserClaim(_x2, _x3) {
    return _ref2.apply(this, arguments);
  };
}();
/**
 * Get the user GUID.
 * @param {string} [accessToken] - The access token (server-side only).
 */


export var getUserGuid = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee3(accessToken) {
    return regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            return _context3.abrupt("return", getUserClaim('sub', accessToken));

          case 1:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function getUserGuid(_x4) {
    return _ref3.apply(this, arguments);
  };
}();
/**
 * Get the user vanity ID.
 * @param {string} [accessToken] - The access token (server-side only).
 */

export var getUserVanityId = /*#__PURE__*/function () {
  var _ref4 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee4(accessToken) {
    var value;
    return regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            _context4.next = 2;
            return getUserClaim('vanity_id', accessToken);

          case 2:
            value = _context4.sent;
            return _context4.abrupt("return", normaliseVanityId(value));

          case 4:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));

  return function getUserVanityId(_x5) {
    return _ref4.apply(this, arguments);
  };
}();
/**
 * Get the identity provider (i.e. login method).
 * @param {string} [accessToken] - The access token (server-side only).
 */

export var getUserLoginMethod = /*#__PURE__*/function () {
  var _ref5 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee5(accessToken) {
    return regeneratorRuntime.wrap(function _callee5$(_context5) {
      while (1) {
        switch (_context5.prev = _context5.next) {
          case 0:
            return _context5.abrupt("return", getUserClaim('idp', accessToken));

          case 1:
          case "end":
            return _context5.stop();
        }
      }
    }, _callee5);
  }));

  return function getUserLoginMethod(_x6) {
    return _ref5.apply(this, arguments);
  };
}();
/**
 * Get the user role.
 * @param {string} [accessToken] - The access token (server-side only).
 */

export var getUserRole = /*#__PURE__*/function () {
  var _ref6 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee6(accessToken) {
    return regeneratorRuntime.wrap(function _callee6$(_context6) {
      while (1) {
        switch (_context6.prev = _context6.next) {
          case 0:
            return _context6.abrupt("return", getUserClaim('role', accessToken));

          case 1:
          case "end":
            return _context6.stop();
        }
      }
    }, _callee6);
  }));

  return function getUserRole(_x7) {
    return _ref6.apply(this, arguments);
  };
}();
/**
 * Get the user email.
 * @param {string} [accessToken] - The access token (server-side only).
 */

export var getUserEmail = /*#__PURE__*/function () {
  var _ref7 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee7(accessToken) {
    return regeneratorRuntime.wrap(function _callee7$(_context7) {
      while (1) {
        switch (_context7.prev = _context7.next) {
          case 0:
            return _context7.abrupt("return", getUserClaim('email', accessToken));

          case 1:
          case "end":
            return _context7.stop();
        }
      }
    }, _callee7);
  }));

  return function getUserEmail(_x8) {
    return _ref7.apply(this, arguments);
  };
}();
/**
 * Check if the user has the admin role.
 * @param {string} [accessToken] - The access token (server-side only).
 */

export var isUserAdmin = /*#__PURE__*/function () {
  var _ref8 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee8(accessToken) {
    var adminRoles;
    return regeneratorRuntime.wrap(function _callee8$(_context8) {
      while (1) {
        switch (_context8.prev = _context8.next) {
          case 0:
            _context8.next = 2;
            return getUserRole(accessToken);

          case 2:
            adminRoles = _context8.sent;

            if (adminRoles) {
              _context8.next = 5;
              break;
            }

            return _context8.abrupt("return", false);

          case 5:
            if (!Array.isArray(adminRoles)) {
              adminRoles = [adminRoles];
            }

            return _context8.abrupt("return", adminRoles.some(function (role) {
              return role.includes('wp-admin');
            }));

          case 7:
          case "end":
            return _context8.stop();
        }
      }
    }, _callee8);
  }));

  return function isUserAdmin(_x9) {
    return _ref8.apply(this, arguments);
  };
}();
/**
 * Should the API client authenticate?
 * @param {'never' | 'always' | 'admin'} authType The authentication type.
 * @param {string} [accessToken] - The access token (server-side only)
 */

export var shouldAuthenticateUser = /*#__PURE__*/function () {
  var _ref9 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee9(authType, accessToken) {
    return regeneratorRuntime.wrap(function _callee9$(_context9) {
      while (1) {
        switch (_context9.prev = _context9.next) {
          case 0:
            _context9.t0 = authType;
            _context9.next = _context9.t0 === 'never' ? 3 : _context9.t0 === 'always' ? 4 : _context9.t0 === 'admin' ? 5 : 6;
            break;

          case 3:
            return _context9.abrupt("return", false);

          case 4:
            return _context9.abrupt("return", true);

          case 5:
            return _context9.abrupt("return", isUserAdmin(accessToken));

          case 6:
            throw new Error("Unknown auth type: ".concat(authType));

          case 7:
          case "end":
            return _context9.stop();
        }
      }
    }, _callee9);
  }));

  return function shouldAuthenticateUser(_x10, _x11) {
    return _ref9.apply(this, arguments);
  };
}();